<script>
import useVuelidate from '@vuelidate/core';

/* COMPOSANTS */
import AkFormSubmitted from '@components/general/AkFormSubmitted';
import GaMessages from '@components/ga/layout/GaMessages';
import AkButtonValidation from '@components/v2/general/AkButtonValidation';
import AkSidebar from '@components/v2/general/AkSidebar';
import AkMissionAssignment from './AkMissionAssignment';
import PrestationBadge from '@components/general/PrestationBadge';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation';

/* MIXINS */
import messageMixin from '@mixins/messageMixin';
import permissionsMixin from '@mixins/permissionsMixin';
import utilsMixin from '@mixins/utilsMixin';
import missionMixin from '@mixins/missionMixin';

/* SERVICES */
import missionPlanningParticipantService from '@services/missionPlanningParticipantService';

export default {
  components: {
    AkPictureAccommodation,
    AkButtonValidation,
    AkSidebar,
    GaMessages,
    AkFormSubmitted,
    PrestationBadge,
    AkMissionAssignment,
  },
  mixins: [messageMixin, permissionsMixin, utilsMixin, missionMixin],
  setup() {
    return {v$: useVuelidate()};
  },
  emits: ['mission-assigned'],
  data() {
    return {
      createSidebarVisible: false,
      missionAssignment: undefined,
      submitted: false,
    };
  },
  validations() {
    return {};
  },
  mounted() {},
  watch: {},
  methods: {
    show(mission) {
      this.createSidebarVisible = true;
      this.initMission(mission);
    },
    initMission(mission) {
      if (!mission) return;
      this.missionAssignment = {...mission};
    },
    hide() {
      this.submitted = false;
      this.missionAssignment = undefined;
      this.createSidebarVisible = false;
    },
    assignMission() {
      this.submitted = true;
      this.$refs.btnValid.load();
      this.v$.$touch();

      if (this.v$.$error) {
        this.$refs.btnValid.init();
        return;
      }

      let mission = {
        prestationId: this.missionAssignment.id,
        assignedStaff: this.missionAssignment.assignedStaff,
        prestationEmployees: this.missionAssignment.prestationEmployees,
        prestationHousekeepers: this.missionAssignment.prestationHousekeepers,
      };

      // Appel WS
      missionPlanningParticipantService
        .assign(mission)
        .then((data) => {
          const nbMissionsEmployee = mission.prestationEmployees?.length ?? 0;
          const nbMissionsHousekeeper = mission.prestationHousekeepers?.length ?? 0;
          const nbParticipants = nbMissionsEmployee + nbMissionsHousekeeper;
          let msg =
            nbParticipants > 1 ? this.$t('planning_intervenant.success') : this.$t('planning_intervenant.success_one');
          this.addSuccess(msg);
          this.$emit('mission-assigned');
          this.hide();
        })
        .catch((e) => {
          let error = e.response ? e.response.data?.error : e;
          this.addError(error);
          this.$refs.btnValid.init();
        })
        .finally(() => {
          this.submitted = false;
        });
    },
  },
  computed: {},
};
</script>

<template>
  <AkSidebar
    :baseZIndex="9998"
    v-model:visible="createSidebarVisible"
    block-scroll
    position="right"
    :dismissable="true">
    <template #container>
      <div class="p-sidebar-header">
        <div class="p-sidebar-header-content">{{ $t('planning_intervenant.schedule_mission') }}</div>
        <button class="p-sidebar-close p-sidebar-icon p-link" type="button" @click="hide">
          <span
            class="p-sidebar-close-icon ga-icon ga-close_circle text-4xl text-grey-400 md:hover:text-grey-900 md:hover:bg-transparent" />
        </button>
      </div>
      <div class="mb-4">
        <GaMessages ref="sidebarMessages" />
      </div>
      <div class="p-sidebar-content">
        <perfect-scrollbar class="h-full flex flex-col gap-4 md:gap-2 pr-7" :options="{suppressScrollX: true}">
          <AkFormSubmitted
            :submitted="this.submitted"
            class="flex flex-col gap-4"
            reference="form"
            @submit="assignMission()">
            <div class="pending-mission w-full flex flex-col bg-grey-50 rounded-xl">
              <AkPictureAccommodation
                disabled-link
                :id="this.missionAssignment.id"
                :md5="this.missionAssignment.accommodationPicture"
                :height="150"
                cover
                radius="rounded-lg"
                :name="
                  this.missionAssignment.accommodationName +
                  ' - ' +
                  this.formatDateIfNeeded(this.missionAssignment.dateStart) +
                  ' - ' +
                  this.missionAssignment.durationDisplay
                "></AkPictureAccommodation>
              <div class="p-2 flex flex-col gap-1">
                <span class="text-grey-900 text-ellipsis block overflow-hidden font-semibold whitespace-nowrap">{{
                  this.missionAssignment.reservationNum
                }}</span>
                <span
                  class="text-grey-900 text-ellipsis overflow-hidden items-center font-normal whitespace-nowrap text-sm flex gap-1">
                  <PrestationBadge :type="this.missionAssignment.type" />
                  <span>-</span>
                  <span> {{ statusDisplay(this.missionAssignment.assignedStaff?.replace(' ', '')) }}</span>
                </span>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <div v-for="(item, idx) in this.missionAssignment.prestationEmployees" :key="item.id">
                <AkMissionAssignment
                  v-model="this.missionAssignment.prestationEmployees[idx]"
                  :index="idx"
                  :dateStart="this.missionAssignment.dateStart"
                  :duration="this.missionAssignment.duration"
                  :companyId="this.missionAssignment.companyId"
                  :assignedStaff="this.missionAssignment.assignedStaff" />
              </div>
              <div v-for="(item, idx) in this.missionAssignment.prestationHousekeepers" :key="item.id">
                <AkMissionAssignment
                  v-model="this.missionAssignment.prestationHousekeepers[idx]"
                  :index="idx"
                  :dateStart="this.missionAssignment.dateStart"
                  :duration="this.missionAssignment.duration"
                  :companyId="this.missionAssignment.companyId"
                  :assignedStaff="this.missionAssignment.assignedStaff" />
              </div>
            </div>
            <div class="flex justify-end items-center gap-6">
              <AkButtonValidation
                class="btn-primary"
                @action="this.assignMission"
                label="Planifier"
                ref="btnValid"></AkButtonValidation>
            </div>
          </AkFormSubmitted>
        </perfect-scrollbar>
      </div>
    </template>
  </AkSidebar>
</template>
